/* FILE: ./frontend/src/features/canada/pages/SupplierInbox.jsx */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { useTranslation, Trans } from 'react-i18next';
import { Helmet } from 'react-helmet';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

const Toast = ({ message, onClose }) => (
  <div className="fixed bottom-5 right-5 bg-slate-900 text-white px-6 py-3 rounded-lg shadow-2xl flex items-center gap-3 animate-bounce-in z-[100] print:hidden">
    <span className="text-green-400 text-xl">✓</span>
    <span className="font-bold">{message}</span>
    <button onClick={onClose} className="ml-4 text-slate-400 hover:text-white" aria-label="Close notification">✕</button>
  </div>
);

export default function SupplierInbox() {
  const { user, userProfile, isSupplier, supplierId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(() => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('tab') === 'support') return 'support';
      if (params.get('tab') === 'selling') return 'selling';
      return 'buying';
  }); 

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedMsg, setSelectedMsg] = useState(null);
  const [liveContacts, setLiveContacts] = useState({ buyer: null, supplier: null }); 
  
  const [replyPrice, setReplyPrice] = useState('');
  const [replyLeadTime, setReplyLeadTime] = useState('');
  const [replyValidity, setReplyValidity] = useState('30 Days');
  const [replyIncoterms, setReplyIncoterms] = useState('EXW (Plant Pick-up)');
  const [replyMessage, setReplyMessage] = useState('');
  
  const [sendingQuote, setSendingQuote] = useState(false);
  const [mode, setMode] = useState('chat'); 
  const [toastMsg, setToastMsg] = useState(null);

  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [newTicketForm, setNewTicketForm] = useState({ subject: '', message: '' });
  const [creatingTicket, setCreatingTicket] = useState(false);

  const [showCreditPaywall, setShowCreditPaywall] = useState(false);
  const [paywallCreditQty, setPaywallCreditQty] = useState(1);
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [filterStatus, setFilterStatus] = useState('active'); 
  const [sortOrder, setSortOrder] = useState('newest'); 

  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [buyerPhone, setBuyerPhone] = useState('');
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [phoneStep, setPhoneStep] = useState('collect'); 
  const [otpCode, setOtpCode] = useState('');

  const chatEndRef = useRef(null);

  useEffect(() => {
      const params = new URLSearchParams(location.search);
      const urlTab = params.get('tab');
      
      if (urlTab === 'support' && activeTab !== 'support') setActiveTab('support');
      else if (urlTab === 'selling' && activeTab !== 'selling') setActiveTab('selling');
      else if (urlTab === 'buying' && activeTab !== 'buying') setActiveTab('buying');
      
      if (activeTab === 'selling' && isSupplier === false) {
          setActiveTab('buying');
      }
  }, [location.search, isSupplier, activeTab]);

  const fetchInbox = async (silent = false) => {
    if (!user) return;
    if (!silent) { setLoading(true); setError(''); }

    try {
      let endpoint = '';
      if (activeTab === 'support') {
          endpoint = `${API_BASE}/inquiries/support`;
      } else if (activeTab === 'selling') {
          if (!isSupplier) { setActiveTab('buying'); return; }
          endpoint = `${API_BASE}/inquiries/selling`;
      } else {
           endpoint = `${API_BASE}/inquiries/buyer`;
      }

      const response = await axios.get(endpoint, { headers: { 'x-user-id': user.id } });
      
      setMessages(prevMessages => {
          const serverMsgs = response.data || [];
          const serverIds = new Set(serverMsgs.map(m => String(m.id)));
          
          const preservedLocalOnly = prevMessages.filter(m => m.is_injected && !serverIds.has(String(m.id)));
          return [...preservedLocalOnly, ...serverMsgs];
      });

    } catch (err) {
      console.error("Inbox Error:", err);
      if (!silent) setError("Failed to load messages.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchInbox();   
  }, [user, isSupplier, supplierId, activeTab]);

  useEffect(() => {
      if (selectedMsg && messages.length > 0) {
          const freshData = messages.find(m => 
              String(m.id) === String(selectedMsg.id) || 
              String(m.base_rfq_id) === String(selectedMsg.id) ||
              String(m.rfq_id) === String(selectedMsg.id)
          );
          
          if (freshData) {
              if (selectedMsg.is_injected && !freshData.is_injected) {
                  setSelectedMsg(freshData);
              }
              else if (!selectedMsg.is_injected && freshData.messages?.length !== selectedMsg.messages?.length) {
                  setSelectedMsg(freshData);
              }
          }
      }
  }, [messages, selectedMsg]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetRfqId = params.get('rfq');
    const targetMode = params.get('mode'); 
    
    if (targetRfqId && !loading && !selectedMsg) {
        const existingThread = messages.find(m => 
            String(m.id) === targetRfqId || 
            String(m.base_rfq_id) === targetRfqId || 
            String(m.rfq_id) === targetRfqId
        );
        
        if (existingThread) {
            setSelectedMsg(existingThread);
            if (targetMode) setMode(targetMode); 
            navigate(`${location.pathname}?tab=${activeTab}`, { replace: true });
        } else if (activeTab === 'selling') {
            const fetchRawRfq = async () => {
                try {
                    const { data, error } = await supabase
                        .from('rfqs')
                        .select('*')
                        .eq('id', targetRfqId)
                        .maybeSingle();

                    if (data && !error) {
                        const injectedMsg = {
                            id: data.id, 
                            is_injected: true, 
                            status: data.status || 'sourcing_active',
                            date: data.created_at,
                            subject: `RFQ: ${data.hs_code}`,
                            body: data.technical_specs,
                            has_quote: false,
                            metadata: {
                                qty: `${data.quantity} ${data.uom || ''}`,
                                location: data.destination_postal_code || data.destination_province,
                                deadline: 'ASAP'
                            },
                            messages: [
                                { 
                                    sender_id: data.buyer_id, 
                                    text: data.technical_specs, 
                                    created_at: data.created_at 
                                }
                            ]
                        };

                        setMessages(prev => {
                            const realArrived = prev.find(m => 
                                String(m.id) === String(injectedMsg.id) || 
                                String(m.base_rfq_id) === String(injectedMsg.id)
                            );
                            
                            if (realArrived && !realArrived.is_injected) {
                                setTimeout(() => setSelectedMsg(realArrived), 0);
                                return prev;
                            }
                            
                            setTimeout(() => setSelectedMsg(injectedMsg), 0);
                            if (prev.some(m => String(m.id) === String(injectedMsg.id))) return prev;
                            return [injectedMsg, ...prev];
                        });
                        
                        if (targetMode) setMode(targetMode);
                        navigate(`${location.pathname}?tab=${activeTab}`, { replace: true });
                    }
                } catch (err) {
                    console.error("Failed to load raw RFQ:", err);
                }
            };
            fetchRawRfq();
        }
    }
  }, [messages, location.search, activeTab, selectedMsg, loading, navigate, location.pathname]);

  useEffect(() => {
      if (selectedMsg?.status === 'accepted') {
          const fetchRichProfiles = async () => {
              try {
                  const otherUserId = selectedMsg.messages?.find(m => m.sender_id !== user?.id)?.sender_id || selectedMsg.target_user_id;
                  const idsToFetch = [user?.id];
                  if (otherUserId) idsToFetch.push(otherUserId);

                  const { data, error } = await supabase.from('profiles').select('*').in('id', idsToFetch);
                  
                  if (data && !error) {
                      const me = data.find(p => p.id === user?.id);
                      const them = data.find(p => p.id === otherUserId);
                      
                      if (activeTab === 'buying') {
                          setLiveContacts({ buyer: me, supplier: them });
                      } else {
                          setLiveContacts({ buyer: them, supplier: me });
                      }
                  }
              } catch (err) {
                  console.error("Failed to fetch rich profile data", err);
              }
          };
          fetchRichProfiles();
      } else {
          setLiveContacts({ buyer: null, supplier: null });
      }
  }, [selectedMsg, user?.id, activeTab]);

  useEffect(() => {
    if (selectedMsg && chatEndRef.current) {
        chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedMsg]);

  const filteredAndSortedMessages = messages
    .filter(msg => {
        const isArchived = msg.status === 'accepted' || msg.status === 'closed';
        if (filterStatus === 'active') return !isArchived;
        if (filterStatus === 'archived') return isArchived;
        return true; 
    })
    .sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  // ⚡ STRICT 100% BLIND RULE: Identities remain masked until the quote is formally accepted
  const getMaskedSubject = (msg) => {
      const isAdmin = userProfile?.role === 'admin';

      if (isAdmin) return msg.subject;
      if (activeTab === 'support') return msg.subject || "Support Request"; 
      if (msg.status === 'accepted') return msg.subject;

      if (activeTab === 'buying') {
          if (msg.subject?.includes('Verified Supplier #') || msg.subject?.includes('[MASTER]')) {
              return msg.subject;
          }
          return msg.subject?.replace(/(Request to ).*/i, '$1Verified Supplier (Hidden)') || 'Request to Verified Supplier (Hidden)';
      } else {
          return msg.subject?.replace(/(Inquiry from ).*/i, '$1Verified Buyer (Hidden)') || msg.subject || 'Inquiry from Verified Buyer (Hidden)';
      }
  };

  const showToast = (msg) => {
      setToastMsg(msg);
      setTimeout(() => setToastMsg(null), 3000); 
  };

  const renderTextWithImages = (text) => {
      if (!text) return "";
      const imageRegex = /\[Image:\s*(https?:\/\/[^\]]+)\]/g;
      const parts = text.split(imageRegex);

      return parts.map((part, idx) => {
          if (idx % 2 === 1) {
              return (
                  <a key={idx} href={part} target="_blank" rel="noreferrer">
                      <img src={part} alt="User attached document or screenshot" className="max-w-[250px] max-h-[250px] object-cover rounded-lg border border-gray-200 my-2 cursor-zoom-in hover:opacity-90 transition-opacity print:max-w-full print:border-none" />
                  </a>
              );
          }
          return <span key={idx} className="whitespace-pre-wrap">{part}</span>;
      });
  };

  const handleCreateTicket = async (e) => {
      e.preventDefault();
      setCreatingTicket(true);
      try {
          await axios.post(`${API_BASE}/inquiries/support/new`, newTicketForm, {
              headers: { 'x-user-id': user.id }
          });
          setShowNewTicketModal(false);
          setNewTicketForm({ subject: '', message: '' });
          showToast("Support ticket created!");
          fetchInbox();
      } catch (err) {
          alert("Failed to create ticket: " + (err.response?.data?.error || err.message));
      } finally {
          setCreatingTicket(false);
      }
  };

  const handleImageUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingImage(true);

      try {
          const fileName = `chat_${user.id}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
          const { error } = await supabase.storage.from('chat-attachments').upload(fileName, file);
          if (error) throw error;
          
          const { data } = supabase.storage.from('chat-attachments').getPublicUrl(fileName);
          setReplyMessage(prev => (prev ? prev + '\n' : '') + `[Image: ${data.publicUrl}]`);
      } catch (err) {
          alert("Failed to upload image: " + err.message);
      } finally {
          setUploadingImage(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
      }
  };

  const initiateAcceptQuote = () => {
      if (userProfile?.phone && userProfile?.phone_verified) {
          handleAcceptQuote();
      } else {
          setShowPhoneModal(true);
      }
  };

  const handleSendOtp = async (e) => {
      e.preventDefault();
      setVerifyingPhone(true);
      try {
          const cleanPhone = buyerPhone.replace(/\D/g, '');
          if (cleanPhone.length < 10) throw new Error(t('auth.err_phone_len', 'Please enter a valid phone number.'));

          await axios.post(`${API_BASE}/verification/send-otp`, {
              phone: cleanPhone,
              email: user.email 
          });

          setPhoneStep('otp');
      } catch (err) {
          alert(err.response?.data?.error || err.message || "Failed to send SMS.");
      } finally {
          setVerifyingPhone(false);
      }
  };

  const handleVerifyOtpAndAccept = async (e) => {
      e.preventDefault();
      setVerifyingPhone(true);
      try {
          const cleanPhone = buyerPhone.replace(/\D/g, '');

          await axios.post(`${API_BASE}/verification/verify-otp`, {
              user_id: user.id,
              phone: cleanPhone,
              code: otpCode
          });

          setShowPhoneModal(false);
          setPhoneStep('collect');
          setOtpCode('');
          await handleAcceptQuote(true); 

      } catch (err) {
          alert(err.response?.data?.error || err.message || "Invalid verification code.");
      } finally {
          setVerifyingPhone(false);
      }
  };

  const handleAcceptQuote = async (skipConfirm = false) => {
      if (!skipConfirm && !window.confirm("Are you sure you want to accept this quote? This will lock the thread for both parties and notify the supplier.")) return;

      setSendingQuote(true);
      try {
          const actualRfqId = selectedMsg.base_rfq_id || selectedMsg.id; 

          await axios.post(`${API_BASE}/inquiries/accept`, {
              rfq_id: actualRfqId,
              target_user_id: selectedMsg.target_user_id || null
          }, { headers: { 'x-user-id': user.id } });

          const phoneToUse = userProfile?.phone || buyerPhone.replace(/\D/g, '');
          const buyerContactStr = `${userProfile?.contact_person || 'Buyer'} | ${userProfile?.company_name || 'Buyer Company'} | +1 ${phoneToUse} | ${user?.email}`;

          const optimisticMsg = {
              id: `temp-accept-${Date.now()}`,
              sender_id: user.id,
              text: `✅ **Quote Accepted**\nThe buyer has accepted the quote.\nNegotiation is now closed. Please proceed with your standard off-platform PO and Invoicing process.\n\n**Buyer Contact:** ${buyerContactStr}\n**Supplier Contact:** Awaiting Server Sync...`,
              created_at: new Date().toISOString()
          };

          setSelectedMsg(prev => ({
              ...prev,
              status: 'accepted',
              messages: [...(prev.messages || []), optimisticMsg]
          }));

          showToast("Quote Accepted & Thread Locked");
          fetchInbox(true);
      } catch (err) {
          alert(`Failed to accept quote: ${err.response?.data?.error || err.message}`);
      } finally {
          setSendingQuote(false);
      }
  };

  let buyerName = "Awaiting Sync", buyerCompany = "", bPhone = "", buyerEmail = "";
  let suppName = "Awaiting Sync", suppCompany = "", sPhone = "", suppEmail = "";

  if (selectedMsg?.status === 'accepted') {
      const acceptMsg = selectedMsg.messages?.find(m => m.text && m.text.includes('✅ **Quote Accepted**'));
      if (acceptMsg) {
          const bMatch = acceptMsg.text.match(/\*\*Buyer Contact:\*\*\s*([^\n]+)/);
          const sMatch = acceptMsg.text.match(/\*\*Supplier Contact:\*\*\s*([^\n]+)/);
          
          if (bMatch) {
              const parts = bMatch[1].split('|').map(p => p.trim());
              if (parts.length === 1 && parts[0].includes('@')) {
                  buyerName = "Buyer"; 
                  buyerEmail = parts[0]; 
              } else {
                  buyerName = parts[0] || "Buyer";
                  buyerCompany = parts[1] || "";
                  bPhone = parts[2] || "";
                  buyerEmail = parts[3] || "";
              }
          }
          if (sMatch) {
              const parts = sMatch[1].split('|').map(p => p.trim());
              if (parts.length === 1 && parts[0].includes('@')) {
                  suppName = "Supplier";
                  suppEmail = parts[0];
              } else {
                  suppName = parts[0] || "Supplier";
                  suppCompany = parts[1] || "";
                  sPhone = parts[2] || "";
                  suppEmail = parts[3] || "";
              }
          }
      }
  }

  const displayBuyerName = liveContacts.buyer?.contact_person || buyerName;
  const displayBuyerComp = liveContacts.buyer?.company_name || buyerCompany;
  const displayBuyerPhone = liveContacts.buyer?.phone || bPhone;
  const displayBuyerEmail = liveContacts.buyer?.email || buyerEmail;

  const displaySuppName = liveContacts.supplier?.contact_person || suppName;
  const displaySuppComp = liveContacts.supplier?.company_name || suppCompany;
  const displaySuppPhone = liveContacts.supplier?.phone || sPhone;
  const displaySuppEmail = liveContacts.supplier?.email || suppEmail;

  const handleAction = async (e) => {
      e.preventDefault();
      setSendingQuote(true);

      try {
          let optimisticMsg = null;
          const actualRfqId = selectedMsg.base_rfq_id || selectedMsg.id; 

          if (mode === 'quote') {
              if (!replyPrice || !replyLeadTime) throw new Error("Price and Lead Time are required for a formal quote.");
              await axios.post(`${API_BASE}/inquiries/quote`, {
                rfq_id: actualRfqId,
                price: replyPrice,
                leadTime: replyLeadTime,
                validity: replyValidity,
                incoterms: replyIncoterms,
                conditions: replyMessage || 'None'
              }, { headers: { 'x-user-id': user.id }});

              optimisticMsg = {
                  id: `temp-${Date.now()}`,
                  sender_id: user.id,
                  text: `📄 **Formal Quote Submitted**\n**Price:** $${replyPrice} CAD\n**Lead Time:** ${replyLeadTime}\n**Valid For:** ${replyValidity}\n**Shipping Terms:** ${replyIncoterms}\n**Notes:** ${replyMessage || 'None'}`,
                  created_at: new Date().toISOString()
              };
              showToast("Official Quote Sent Successfully");
          } else {
              if (!replyMessage) throw new Error("Message cannot be empty.");
              const replyEndpoint = activeTab === 'support' ? `${API_BASE}/inquiries/support/reply` : `${API_BASE}/inquiries/reply`;
              
              await axios.post(replyEndpoint, {
                inquiry_id: actualRfqId,
                message: replyMessage,
                target_user_id: selectedMsg.target_user_id || null 
              }, { headers: { 'x-user-id': user.id }});

              optimisticMsg = {
                  id: `temp-${Date.now()}`,
                  sender_id: user.id,
                  text: replyMessage,
                  created_at: new Date().toISOString()
              };
              showToast("Message Sent"); 
          }

          if (optimisticMsg) {
              setSelectedMsg(prev => ({
                  ...prev,
                  messages: [...(prev.messages || []), optimisticMsg],
                  has_quote: mode === 'quote' ? true : prev.has_quote
              }));
          }

          setReplyPrice('');
          setReplyLeadTime('');
          setReplyValidity('30 Days');
          setReplyIncoterms('EXW (Plant Pick-up)');
          setReplyMessage('');
          setMode('chat');
          
          fetchInbox(true);
      } catch (err) {
          if (err.response?.data?.code === 'INSUFFICIENT_CREDITS') {
              setShowCreditPaywall(true);
          } else {
              alert(`Failed to send: ${err.response?.data?.error || err.message}`);
          }
      } finally {
          setSendingQuote(false);
      }
  };

  const MessageBubble = ({ m, isMe }) => {
    const isSystemQuote = m.text.startsWith('📄 **Formal Quote Submitted**') || m.text.startsWith('📄 **Official Quote:**');
    const isSystemAccept = m.text.startsWith('✅ **Quote Accepted**'); 

    if (isSystemAccept) {
        return (
            <div className="flex justify-center my-4 print:my-2 print:block print:w-full print:break-inside-avoid">
                <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-sm text-green-900 shadow-md max-w-lg w-full text-left print:shadow-none print:border-gray-300 print:max-w-full">
                    <div className="font-black border-b border-green-200 pb-2 mb-3 text-center tracking-wider text-green-800 print:text-black">✅ NEGOTIATION CLOSED</div>
                    <div className="space-y-2 whitespace-pre-wrap leading-relaxed print:text-black">
                        {m.text.replace(/✅ \*\*.*?\*\*\n/, '').replace(/\*\*.*Contact:\*\*.*/g, '').trim().split('\n').filter(l => l.trim()).map((line, idx) => (
                            <p key={idx} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (isSystemQuote) {
        return (
            <div className="flex justify-center my-4 print:my-2 print:block print:w-full print:break-inside-avoid">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 text-sm text-yellow-900 shadow-md max-w-sm w-full text-left print:shadow-none print:border-gray-300 print:max-w-full">
                    <div className="font-black border-b border-yellow-200 pb-2 mb-3 text-center tracking-wider text-yellow-800 print:text-black">📜 OFFICIAL QUOTE</div>
                    <div className="space-y-2 whitespace-pre-wrap leading-relaxed print:text-black">
                        {m.text.replace(/📄 \*\*.*?\*\*\n/, '').trim().split('\n').map((line, idx) => (
                            <p key={idx} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                        ))}
                    </div>
                    <div className="text-[10px] text-yellow-600 mt-4 text-right border-t border-yellow-200 pt-2 print:text-gray-500">
                        {new Date(m.created_at).toLocaleString()}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} my-2 print:my-1 print:block print:w-full print:break-inside-avoid`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm shadow-sm print:shadow-none print:border print:border-gray-300 print:max-w-full ${
                isMe ? 'bg-blue-600 text-white rounded-br-none print:bg-white print:text-black' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none print:text-black'
            }`}>
                <div className="leading-relaxed">{renderTextWithImages(m.text)}</div>
                <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-200 print:text-gray-500' : 'text-gray-400 print:text-gray-500'}`}>
                    {new Date(m.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </p>
            </div>
        </div>
    );
  };

  if (loading && messages.length === 0) return <div className="p-12 text-center animate-pulse">{t('inbox.loading')}</div>;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 relative print:bg-white print:py-0">
      
      <style>{`
        @media print {
          body, html, #root {
            height: auto !important;
            min-height: auto !important;
            overflow: visible !important;
            position: static !important;
            display: block !important;
            background-color: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .print-force-block {
            display: block !important;
            position: static !important;
            height: auto !important;
            overflow: visible !important;
            width: 100% !important;
            background: white !important;
          }
        }
      `}</style>

      <Helmet>
        <title>{t('inbox.title', 'Message Center')} | TariffBeater</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg(null)} />}

      <div className="max-w-5xl mx-auto print:hidden">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('inbox.title')}</h1>
            <p className="text-gray-500 text-sm">{t('inbox.subtitle')}</p>
          </div>
          
          <div className="flex items-center gap-3">
             {activeTab === 'support' && (
                <button 
                    onClick={() => setShowNewTicketModal(true)}
                    className="bg-slate-800 hover:bg-black text-white px-4 py-2 rounded-md text-sm font-bold shadow-sm transition-all flex items-center gap-1"
                >
                    {t('inbox.new_ticket')}
                </button>
             )}

             <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-200 flex flex-wrap gap-1">
                <button
                   onClick={() => setActiveTab('buying')}
                   className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${activeTab === 'buying' ? 'bg-blue-100 text-blue-800 shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                   {t('inbox.tab_buying')}
                </button>
                {isSupplier && (
                    <button
                       onClick={() => setActiveTab('selling')}
                       className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${activeTab === 'selling' ? 'bg-green-100 text-green-800 shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        {t('inbox.tab_selling')}
                    </button>
                )}
                <button
                   onClick={() => setActiveTab('support')}
                   className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${activeTab === 'support' ? 'bg-slate-800 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                   {t('inbox.tab_support')}
                </button>
             </div>
          </div>
        </div>

        {error && <div className="bg-red-50 text-red-700 p-4 rounded mb-4">{error}</div>}

        {messages.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-3 rounded-lg border border-gray-200 shadow-sm mb-4 gap-4">
              <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-500">Show:</span>
                  <select 
                      value={filterStatus} 
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 py-1.5 pl-3 pr-8"
                  >
                      <option value="active">Active Threads</option>
                      <option value="archived">Completed / Closed</option>
                      <option value="all">All Messages</option>
                  </select>
              </div>
              <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-500">Sort:</span>
                  <select 
                      value={sortOrder} 
                      onChange={(e) => setSortOrder(e.target.value)}
                      className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 py-1.5 pl-3 pr-8"
                  >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                  </select>
              </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[400px]">
          {messages.length === 0 ? (
            <div className="p-16 text-center text-gray-400">
              <div className="text-5xl mb-4">{activeTab === 'buying' ? '📤' : activeTab === 'support' ? '🎧' : '📥'}</div>
              <h3 className="text-lg font-medium text-gray-900">{t('inbox.no_messages')}</h3>
              <p className="mb-6">
                  {activeTab === 'buying' ? t('inbox.no_quotes_req') : 
                   activeTab === 'support' ? t('inbox.no_tickets') :
                   t('inbox.no_inquiries')}
              </p>
              
              {activeTab === 'support' && (
                  <button 
                      onClick={() => setShowNewTicketModal(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm transition-all"
                  >
                      {t('inbox.open_ticket_btn')}
                  </button>
              )}
            </div>
          ) : filteredAndSortedMessages.length === 0 ? (
            <div className="p-16 text-center text-gray-400">
              <div className="text-5xl mb-4">📭</div>
              <h3 className="text-lg font-medium text-gray-900">No {filterStatus === 'active' ? 'Active' : 'Closed'} threads found.</h3>
              <p className="mb-6">You have messages, but none match your current filter.</p>
              <button 
                  onClick={() => setFilterStatus('all')}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2.5 rounded-lg font-bold shadow-sm transition-all"
              >
                  Clear Filters
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredAndSortedMessages.map((msg) => {
                return (
                  <div key={msg.id} className="p-6 hover:bg-blue-50 transition-colors group cursor-pointer" onClick={() => setSelectedMsg(msg)}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wide ${
                            activeTab === 'support' ? 'bg-slate-800 text-white' : 
                            activeTab === 'selling' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {activeTab === 'support' ? t('inbox.support_ticket') : activeTab === 'selling' ? t('inbox.incoming_req') : t('inbox.sent_req')}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">{new Date(msg.date).toLocaleDateString()}</span>
                      </div>
                      
                      <div className="flex gap-2">
                          {msg.status === 'accepted' && <span className="bg-gray-800 text-white text-xs font-bold px-2 py-1 rounded">{t('inbox.status_won')}</span>}
                          {msg.status === 'closed' && activeTab === 'support' && <span className="bg-gray-200 text-gray-600 text-xs font-bold px-2 py-1 rounded">{t('inbox.status_resolved')}</span>}
                          {msg.status !== 'accepted' && msg.has_quote && <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded border border-yellow-200">{t('inbox.status_quote')}</span>}
                          {(msg.status === 'replied' || msg.status === 'open' || msg.status === 'sourcing_active') && <span className="text-blue-600 text-xs font-bold">{t('inbox.status_active', 'Active')}</span>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">
                      <div className="md:col-span-3">
                        <h3 className="text-lg font-bold text-gray-800">
                            {getMaskedSubject(msg)}
                        </h3>
                        <p className="text-gray-600 mt-1 text-sm line-clamp-1">{msg.body || msg.specs_text}</p>
                        {activeTab === 'buying' && msg.latest_quote && (
                            <div className="mt-2 text-sm text-gray-600">
                                {t('inbox.latest_offer')} <span className="font-bold text-green-700">${msg.latest_quote.price_quoted} CAD</span>
                            </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-end">
                        <button className="text-blue-600 hover:text-blue-800 font-bold text-sm border border-blue-200 px-4 py-2 rounded hover:bg-blue-50">
                          {t('inbox.open_thread')}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectedMsg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm print-force-block">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full h-[85vh] flex flex-col overflow-hidden animate-fade-in-up print-force-block print:shadow-none print:border-none print:rounded-none print:m-0 print:p-0">
            
            <div className={`text-white px-6 py-4 flex justify-between items-center shrink-0 print:bg-white print:text-black print:border-b-2 print:border-black print:block ${activeTab === 'support' ? 'bg-slate-800' : 'bg-slate-900'}`}>
              <div>
                  <h3 className="font-bold text-lg print:text-2xl">
                      {activeTab === 'support' ? `Ticket: ${selectedMsg.subject}` : getMaskedSubject(selectedMsg)}
                  </h3>
                  <div className="text-xs text-slate-300 flex gap-2 print:text-gray-600 mt-1">
                      <span>{t('inbox.id')} {selectedMsg.id.slice(0,8)}</span>
                      <span>•</span>
                      <span className="font-bold">
                          {activeTab === 'support' ? t('inbox.support_channel') : activeTab === 'selling' ? t('inbox.supplier_view') : t('inbox.buyer_view')}
                      </span>
                  </div>
              </div>
              <button onClick={() => setSelectedMsg(null)} className="text-gray-400 hover:text-white text-2xl print:hidden" aria-label="Close message">×</button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-4 print-force-block print:p-0">
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mx-auto max-w-2xl print-force-block print:border-2 print:border-gray-800 print:shadow-none print:break-inside-avoid">
                    <div className="flex justify-between items-center mb-2 border-b border-gray-100 pb-2 print:block print:border-b-2 print:border-black">
                       <span className="text-xs font-bold text-blue-600 uppercase print:text-black print:block print:mb-1">
                            {activeTab === 'support' ? t('inbox.issue_desc') : t('inbox.original_req')}
                        </span>
                        <span className="text-xs text-gray-400 print:text-black print:block">{new Date(selectedMsg.date).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="text-sm text-gray-800 whitespace-pre-wrap print:mt-4">
                        {renderTextWithImages(selectedMsg.body || selectedMsg.specs_text)}
                    </div>
                    
                    {selectedMsg.metadata && activeTab !== 'support' && (
                        <div className="mt-4 pt-3 border-t border-dashed border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs print:block print:border-t-2 print:border-black print:mt-6 print:pt-4">
                            <div className="bg-gray-50 p-2 rounded print:inline-block print:w-1/4 print:p-0 print:align-top">
                                <span className="block text-gray-400 font-bold uppercase text-[10px] print:text-black">{t('inbox.qty')}</span>
                                <span className="font-mono text-gray-800 font-bold print:text-black print:text-sm">{selectedMsg.metadata.qty || t('inbox.no_loc')}</span>
                            </div>
                            <div className="bg-gray-50 p-2 rounded col-span-2 print:inline-block print:w-1/2 print:p-0 print:align-top">
                                <span className="block text-gray-400 font-bold uppercase text-[10px] print:text-black">{t('inbox.dest')}</span>
                                <span className="font-bold text-blue-800 print:text-black print:text-sm">{selectedMsg.metadata.location || t('inbox.no_loc')}</span>
                            </div>
                            <div className="bg-gray-50 p-2 rounded print:inline-block print:w-1/4 print:p-0 print:align-top">
                                <span className="block text-gray-400 font-bold uppercase text-[10px] print:text-black">{t('inbox.timeline')}</span>
                                <span className="font-mono text-gray-800 font-bold print:text-black print:text-sm">{selectedMsg.metadata.deadline || t('inbox.asap')}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-2 max-w-3xl mx-auto print-force-block print:mt-8">
                  {selectedMsg.messages?.map((m, i) => {
                        if (i === 0 && m.text === (selectedMsg.body || selectedMsg.specs_text)) return null;
                        return <MessageBubble key={i} m={m} isMe={m.sender_id === user.id} />;
                  })}
                    <div ref={chatEndRef} />
                </div>
            </div>

            <div className="bg-white border-t border-gray-200 p-4 shrink-0 print-force-block print:border-none print:mt-8">
              {selectedMsg.status === 'accepted' ? (
                    <div className="bg-slate-100 border border-slate-300 p-6 rounded-xl text-center max-w-3xl mx-auto shadow-inner print-force-block print:bg-white print:border-none print:p-0 print:break-inside-avoid">
                        <h4 className="text-slate-900 font-black text-xl flex items-center justify-center gap-2 tracking-wide print:block print:text-center print:text-2xl print:border-b-2 print:border-black print:pb-4 print:mb-4">
                            <span className="print:hidden">🔒</span> {t('inbox.neg_closed')}
                        </h4>
                        <p className="text-slate-700 text-sm mt-2 font-medium print:text-black print:mb-6">
                            {t('inbox.locked_desc')}
                        </p>

                        <div className="mt-6 mb-2 bg-white border border-gray-200 rounded-lg p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 shadow-sm print:block print:border-2 print:border-black print:p-6 print:shadow-none">
                            <div className="flex-1 text-left w-full print:inline-block print:w-[45%] print:align-top">
                                <span className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 print:text-black print:text-xs">Buyer Details</span>
                                <div className="font-black text-gray-900 text-lg print:text-black print:text-xl">{displayBuyerName}</div>
                                {displayBuyerComp && <div className="text-gray-700 font-medium text-sm mb-2 print:text-black print:text-base">{displayBuyerComp}</div>}
                                {displayBuyerEmail && <div className="text-blue-700 text-sm font-semibold hover:underline cursor-pointer print:text-black print:mt-2"><a href={`mailto:${displayBuyerEmail}`}>{displayBuyerEmail}</a></div>}
                                {displayBuyerPhone && <div className="text-gray-600 text-sm font-mono mt-1 print:text-black">{displayBuyerPhone}</div>}
                            </div>
                            <div className="hidden md:block w-px h-20 bg-gray-200 print:inline-block print:w-[10%] print:bg-transparent"></div>
                            <div className="flex-1 text-left md:text-right w-full print:inline-block print:w-[45%] print:align-top print:text-right">
                                <span className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 print:text-black print:text-xs">Supplier Details</span>
                                <div className="font-black text-gray-900 text-lg print:text-black print:text-xl">{displaySuppName}</div>
                                {displaySuppComp && <div className="text-gray-700 font-medium text-sm mb-2 print:text-black print:text-base">{displaySuppComp}</div>}
                                {displaySuppEmail && <div className="text-blue-700 text-sm font-semibold hover:underline cursor-pointer print:text-black print:mt-2"><a href={`mailto:${displaySuppEmail}`}>{displaySuppEmail}</a></div>}
                                {displaySuppPhone && <div className="text-gray-600 text-sm font-mono mt-1 print:text-black">{displaySuppPhone}</div>}
                            </div>
                        </div>
                        
                        <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left text-xs text-amber-900 leading-relaxed shadow-sm print:block print:border-2 print:border-black print:text-black print:bg-white print:mt-8">
                            <span className="font-black block mb-1 uppercase tracking-wider text-[10px] text-amber-700 print:text-black print:text-xs print:mb-2">
                                {t('inbox.legal_notice', 'LEGAL & DATA RETENTION NOTICE')}
                            </span>
                            <p className="print:text-black">
                                {t('inbox.legal_desc_1', 'TariffBeater is a venue-only platform and is not a party to this transaction. We do not guarantee permanent data retention for closed threads.')}
                                <br/><br/>
                                <b>{t('inbox.legal_desc_2', 'You must export or print this page for your own official accounting and tax records.')}</b> {t('inbox.legal_desc_3', 'By proceeding off-platform, you agree that TariffBeater Inc. holds no liability for the fulfillment, quality, or payment of these goods.')}
                            </p>
                        </div>
                        
                        <button 
                            onClick={() => window.print()}
                            className="mt-5 bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-800 hover:text-white hover:border-slate-800 px-6 py-2.5 rounded-lg shadow-sm text-sm font-bold transition-all flex items-center gap-2 mx-auto print:hidden"
                        >
                            <span>🖨️</span> {t('inbox.print_btn')}
                        </button>
                    </div>
                ) : selectedMsg.status === 'closed' && activeTab === 'support' ? (
                    <div className="text-center text-gray-500 py-4 font-bold">{t('inbox.ticket_resolved')}</div>
                ) : (
                    <div className="max-w-3xl mx-auto">
                        {activeTab === 'buying' && selectedMsg.has_quote && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col md:flex-row justify-between items-center gap-3">
                                <span className="text-sm text-blue-900 font-medium">{t('inbox.ready_proceed')}</span>
                                
                                <button 
                                    onClick={initiateAcceptQuote}
                                    disabled={sendingQuote}
                                    className="bg-green-600 text-white px-5 py-2 rounded-lg font-bold hover:bg-green-700 shadow-sm transition-all w-full md:w-auto"
                                >
                                {t('inbox.accept_quote')}
                                </button>
                            </div>
                        )}

                        {activeTab === 'selling' && (
                            <div className="flex gap-2 mb-3">
                                <button 
                                    onClick={() => setMode('chat')}
                                    className={`px-3 py-1 rounded-full text-xs font-bold ${mode === 'chat' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}
                                >
                                    {t('inbox.mode_msg')}
                                </button>
                                <button 
                                    onClick={() => setMode('quote')}
                                    className={`px-3 py-1 rounded-full text-xs font-bold ${mode === 'quote' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-500'}`}
                                >
                                    {t('inbox.mode_quote')}
                                </button>
                            </div>
                        )}

                        <form onSubmit={handleAction}>
                            {mode === 'quote' ? (
                                <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-300 shadow-sm">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                                        <div>
                                            <label htmlFor="quote_price" className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">{t('inbox.price_cad')}</label>
                                            <input 
                                                id="quote_price"
                                                type="number" required min="0" step="0.01"
                                                className="w-full border-yellow-300 rounded p-2 text-sm focus:ring-yellow-500"
                                                placeholder="0.00"
                                                value={replyPrice}
                                                onChange={e => setReplyPrice(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="quote_lead" className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">{t('inbox.lead_time')}</label>
                                            <input 
                                                id="quote_lead"
                                                type="text" required
                                                className="w-full border-yellow-300 rounded p-2 text-sm focus:ring-yellow-500"
                                                placeholder="e.g. 4 Weeks ARO"
                                                value={replyLeadTime}
                                                onChange={e => setReplyLeadTime(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="quote_validity" className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">{t('inbox.valid_for')}</label>
                                            <select 
                                                id="quote_validity"
                                                className="w-full border-yellow-300 rounded p-2 text-sm focus:ring-yellow-500 bg-white"
                                                value={replyValidity}
                                                onChange={e => setReplyValidity(e.target.value)}
                                            >
                                                <option>7 Days</option>
                                                <option>14 Days</option>
                                                <option>30 Days</option>
                                                <option>60 Days</option>
                                            </select>
                                        </div>
                                        <div>
                                            {/* ⚡ FIX: Updated dropdown to Canadian Domestic Shipping Terms */}
                                            <label htmlFor="quote_incoterms" className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">{t('inbox.shipping_terms')}</label>
                                            <select 
                                                id="quote_incoterms"
                                                className="w-full border-yellow-300 rounded p-2 text-sm focus:ring-yellow-500 bg-white"
                                                value={replyIncoterms}
                                                onChange={e => setReplyIncoterms(e.target.value)}
                                            >
                                                <option>EXW (Plant Pick-up)</option>
                                                <option>FOB Plant (Buyer arranges freight)</option>
                                                <option>FOB Jobsite (Supplier delivers to site)</option>
                                                <option>Freight Included / Delivered</option>
                                                <option>Freight Prepaid & Add (Billed to Buyer)</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex gap-3 items-start">
                                        <div className="flex-1">
                                            <label htmlFor="quote_notes" className="block text-[10px] font-bold text-yellow-800 uppercase mb-1">{t('inbox.add_notes')}</label>
                                            <textarea 
                                                id="quote_notes"
                                                rows="2"
                                                className="w-full border-yellow-300 rounded p-2 text-sm focus:ring-yellow-500 resize-none"
                                                placeholder="e.g. Price excludes taxes. Payment net 30 days."
                                                value={replyMessage}
                                                onChange={e => setReplyMessage(e.target.value)}
                                            />
                                        </div>
                                        <button 
                                            type="submit" 
                                            disabled={sendingQuote}
                                            className="bg-yellow-600 text-white px-6 py-4 rounded-lg font-bold hover:bg-yellow-700 mt-5 text-sm shrink-0 shadow-sm"
                                        >
                                            {t('inbox.send_quote')}
                                        </button>
                                     </div>
                                </div>
                            ) : (
                                <div className="flex gap-2 items-end">
                                    <div className="flex-1 bg-white border border-gray-300 rounded-lg shadow-sm flex items-end p-2 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                                        <button 
                                            type="button" 
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={sendingQuote || uploadingImage}
                                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                            title="Attach Image / Screenshot"
                                            aria-label="Attach file to message"
                                        >
                                            📎
                                        </button>
                                        <input 
                                            type="file" 
                                            ref={fileInputRef} 
                                            onChange={handleImageUpload} 
                                            className="hidden" 
                                            accept="image/*"
                                            aria-label="File upload input"
                                        />
                                        <textarea 
                                            rows="1" required
                                            className="flex-1 border-none focus:ring-0 text-sm resize-none p-2 bg-transparent outline-none"
                                            placeholder={uploadingImage ? t('inbox.uploading_attach') : t('inbox.type_msg')}
                                            value={replyMessage}
                                            onChange={e => setReplyMessage(e.target.value)}
                                            aria-label={t('inbox.type_msg')}
                                        />
                                    </div>
                                    <button 
                                        type="submit" 
                                        disabled={sendingQuote || uploadingImage}
                                        className="bg-blue-600 text-white px-8 py-2 rounded-lg font-bold hover:bg-blue-700 transition-colors self-end shrink-0 shadow-sm disabled:opacity-50"
                                    >
                                        {t('inbox.send')}
                                    </button>
                                </div>
                               )}
                        </form>
                    </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* THE NEW TICKET MODAL */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-[100] backdrop-blur-sm animate-fade-in print:hidden">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                <div className="px-6 py-4 bg-slate-800 text-white flex justify-between items-center">
                    <h3 className="font-bold text-lg">{t('inbox.create_ticket')}</h3>
                    <button onClick={() => setShowNewTicketModal(false)} className="text-slate-300 hover:text-white text-2xl" aria-label="Close modal">×</button>
                </div>
                <form onSubmit={handleCreateTicket} className="p-6 space-y-4">
                    <div>
                        <label htmlFor="ticket_subject" className="block text-xs font-bold text-gray-700 uppercase mb-1">{t('inbox.subject')}</label>
                        <input 
                            id="ticket_subject"
                            type="text" required 
                            value={newTicketForm.subject} 
                            onChange={e => setNewTicketForm({...newTicketForm, subject: e.target.value})} 
                            className="w-full border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500" 
                            placeholder="e.g., Issue adding a new product" 
                        />
                    </div>
                    <div>
                        <label htmlFor="ticket_message" className="block text-xs font-bold text-gray-700 uppercase mb-1">{t('inbox.msg_desc')}</label>
                        <textarea 
                            id="ticket_message"
                            required rows="4" 
                            value={newTicketForm.message} 
                             onChange={e => setNewTicketForm({...newTicketForm, message: e.target.value})} 
                            className="w-full border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 resize-none" 
                            placeholder="Please describe your issue in detail..." 
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-4">
                        <button type="button" onClick={() => setShowNewTicketModal(false)} className="px-4 py-2 text-gray-500 font-bold hover:text-gray-800">{t('inbox.cancel')}</button>
                        <button type="submit" disabled={creatingTicket} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 shadow-md transition-colors disabled:opacity-50">
                            {creatingTicket ? t('inbox.submitting') : t('inbox.submit_ticket')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* ⚡ THE SYNCHRONIZED PAYWALL MODAL ($39/credit Custom Quantity + $199/mo 10 RFQ Credits/mo) */}
      {showCreditPaywall && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-[60] backdrop-blur-sm p-4 animate-fade-in print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-8 text-center border-b border-gray-100">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">💎</div>
              <h2 className="text-2xl font-black text-gray-900 tracking-tight">{t('inbox.unlock_rfq')}</h2>
              <p className="text-gray-600 mt-3 text-sm leading-relaxed">
                You need <strong className="text-gray-900">1 RFQ Reply Credit</strong> to respond to this RFQ (covers both messages and formal quotes on this thread). Buyer contact details are unlocked when your quote is accepted.
              </p>
            </div>
            <div className="p-6 bg-gray-50 space-y-4">
              <div className="bg-white border-2 border-blue-600 p-4 rounded-xl shadow-sm space-y-3">
                <div className="flex justify-between items-center">
                  <div className="text-left">
                    <div className="text-base font-bold text-blue-900">Buy A-La-Carte RFQ Credits</div>
                    <div className="text-xs text-blue-600 font-medium">$39 CAD per credit • Valid until cycle end</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="paywall_qty" className="text-xs font-bold text-gray-500">Qty:</label>
                    <input
                      id="paywall_qty"
                      type="number"
                      min="1"
                      max="500"
                      value={paywallCreditQty}
                      onChange={(e) => setPaywallCreditQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      className="w-16 text-center font-mono font-bold text-sm border border-gray-300 rounded p-1.5"
                    />
                  </div>
                </div>
                <button 
                  onClick={() => { setShowCreditPaywall(false); navigate(`/billing?modules=rfq_credit_single&qty=${paywallCreditQty}`); }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg font-bold text-sm flex justify-between items-center transition-all shadow-sm"
                >
                  <span>Purchase {paywallCreditQty} {paywallCreditQty === 1 ? 'Credit' : 'Credits'}</span>
                  <span>${paywallCreditQty * 39} CAD →</span>
                </button>
              </div>

              <button 
                onClick={() => { setShowCreditPaywall(false); navigate('/billing?modules=premium_supplier'); }}
                className="w-full bg-gradient-to-r from-slate-900 to-indigo-900 hover:from-black hover:to-indigo-950 text-white p-4 rounded-xl font-bold flex justify-between items-center transition-all group shadow-md"
              >
                <div className="text-left">
                  <div className="text-base flex items-center gap-2">
                    {t('inbox.prem_sup')} <span className="bg-yellow-400 text-yellow-900 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide">{t('inbox.best_value')}</span>
                  </div>
                  <div className="text-xs text-blue-200 font-medium mt-0.5">10 RFQ Credits/mo + Unmasked Analytics + 1,000 SKUs</div>
                </div>
                <div className="text-xl group-hover:scale-110 transition-transform">$199<span className="text-sm font-normal opacity-75">/mo</span></div>
               </button>

               <p className="text-[11px] text-gray-500 text-center leading-snug pt-1">
                 Note: Monthly allowances and a-la-carte credits are valid until the end of your current monthly billing period and never roll over.
               </p>
            </div>
            <div className="p-4 text-center">
              <button 
                onClick={() => setShowCreditPaywall(false)}
                className="text-gray-500 hover:text-gray-800 font-medium text-sm transition-colors"
             >
                {t('inbox.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* JUST-IN-TIME PHONE VERIFICATION MODAL */}
      {showPhoneModal && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-[110] backdrop-blur-sm p-4 animate-fade-in print:hidden">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-slate-800 text-white flex justify-between items-center">
                    <h3 className="font-bold text-lg">{t('inbox.finalize_rfq', 'Finalize Your RFQ')}</h3>
                    <button onClick={() => { setShowPhoneModal(false); setPhoneStep('collect'); }} className="text-slate-300 hover:text-white text-2xl" aria-label="Close modal">×</button>
                </div>
                
                {phoneStep === 'collect' ? (
                    <form onSubmit={handleSendOtp} className="p-6 space-y-4">
                        <div className="text-gray-600 text-sm mb-4">
                            <Trans i18nKey="inbox.phone_reason">
                                You are about to accept this quote. To introduce you to the supplier's sales team and finalize shipping details, please provide and verify your direct phone line.
                            </Trans>
                        </div>
                        <div>
                            <label htmlFor="buyer_phone" className="block text-xs font-bold text-gray-700 uppercase mb-1">{t('auth.phone', 'Phone Number')}</label>
                            <input
                                id="buyer_phone"
                                type="tel" required
                                value={buyerPhone}
                                onChange={e => setBuyerPhone(e.target.value)}
                                className="w-full border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500"
                                placeholder="+1 (555) 123-4567"
                            />
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button type="button" onClick={() => setShowPhoneModal(false)} className="px-4 py-2 text-gray-500 font-bold hover:text-gray-800">
                                {t('inbox.cancel', 'Cancel')}
                            </button>
                            <button type="submit" disabled={verifyingPhone} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 shadow-md transition-colors disabled:opacity-50">
                                {verifyingPhone ? t('inbox.sending_sms', 'Sending SMS...') : t('inbox.send_code', 'Send Code')}
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleVerifyOtpAndAccept} className="p-6 space-y-4 text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                            <span className="text-xl">📱</span>
                        </div>
                        <h3 className="font-bold text-gray-900">{t('auth.verify_phone_title', 'Verify Your Number')}</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {t('auth.verify_phone_desc', 'Enter the 6-digit code sent to')} <span className="font-bold">{buyerPhone}</span>
                        </p>
                        
                        <input
                            type="text" required maxLength="6" autoFocus
                            value={otpCode}
                            onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            className="block w-full max-w-[200px] mx-auto text-center text-3xl tracking-[0.5em] py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="------"
                            aria-label="OTP verification code"
                        />
                        
                        <div className="flex justify-end gap-3 mt-6">
                            <button type="button" onClick={() => setPhoneStep('collect')} className="px-4 py-2 text-gray-500 font-bold hover:text-gray-800 text-sm">
                                {t('auth.change_phone', 'Change Number')}
                            </button>
                            <button type="submit" disabled={verifyingPhone || otpCode.length !== 6 || sendingQuote} className="bg-green-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-green-700 shadow-md transition-colors disabled:opacity-50">
                                {verifyingPhone || sendingQuote ? t('inbox.processing', 'Processing...') : t('inbox.confirm_award', 'Confirm & Award')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
      )}

    </div>
  );
}